/*
 * Client-side SKU / partial-code search.
 *
 * Shopify's native storefront search (and the predictive/suggest API) only
 * matches whole word-tokens or the full exact SKU. A continuous fragment like
 * "lovu0" can never match a SKU stored as "LOVU099IRI" because the engine
 * splits it into separate tokens. This module restores "contains" matching by
 * building a small client-side index of {title, handle, sku, image, price}
 * from /products.json (cached in localStorage) and doing substring matching in
 * the browser. It augments the predictive dropdown and provides a fallback on
 * the search results page when native search returns nothing.
 */
(function () {
  'use strict';

  var CONFIG = {
    cacheKey: 'mjm:sku-index:v1',
    cacheTtlMs: 6 * 60 * 60 * 1000, // 6 hours
    pageSize: 250,
    maxPages: 60, // safety cap (250 * 60 = 15,000 products)
    minQueryLength: 2,
    dropdownLimit: 6,
    pageLimit: 60
  };

  function normalizeCompact(value) {
    return (value || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function shopRoot() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root.replace(/\/$/, '');
  }

  function productUrl(handle) {
    return shopRoot() + '/products/' + handle;
  }

  function sizedImage(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function formatPrice(price) {
    var n = Number(price);
    if (isNaN(n)) return '';
    try {
      return n.toLocaleString(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' });
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  function handleFromHref(href) {
    if (!href) return '';
    var m = href.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function escapeHtml(str) {
    return (str || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function spinnerHtml(label) {
    return (
      '<span class="m-sku-search-loading__inner" role="status" aria-live="polite">' +
      '<span class="m-spinner-icon m-sku-search-loading__spinner">' +
      '<svg class="m-svg-icon--medium" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
      '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2.5" opacity="0.25"></circle>' +
      '<path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>' +
      '</svg></span>' +
      (label ? '<span class="m-sku-search-loading__label">' + escapeHtml(label) + '</span>' : '') +
      '</span>'
    );
  }

  function showLoading(el, label) {
    if (!el || el.querySelector('[data-sku-search-loading]')) return;
    el.insertAdjacentHTML(
      'beforeend',
      '<div class="m-sku-search-loading" data-sku-search-loading>' + spinnerHtml(label) + '</div>'
    );
  }

  function hideLoading(el) {
    if (!el) return;
    var node = el.querySelector('[data-sku-search-loading]');
    if (node) node.remove();
  }

  // --- Index store -----------------------------------------------------------
  var Index = {
    ready: false,
    loading: null,
    items: [],

    _readCache: function () {
      try {
        var raw = localStorage.getItem(CONFIG.cacheKey);
        if (!raw) return null;
        var parsed = JSON.parse(raw);
        if (!parsed || !parsed.ts || (Date.now() - parsed.ts) > CONFIG.cacheTtlMs) return null;
        return parsed.items || null;
      } catch (e) {
        return null;
      }
    },

    _writeCache: function (items) {
      try {
        localStorage.setItem(CONFIG.cacheKey, JSON.stringify({ ts: Date.now(), items: items }));
      } catch (e) {
        /* quota exceeded - keep in-memory only */
      }
    },

    _prepare: function (items) {
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        it._sku = normalizeCompact((it.s || []).join(' '));
        it._title = (it.t || '').toLowerCase();
      }
      this.items = items;
      this.ready = true;
    },

    _fetchAll: function () {
      var self = this;
      var collected = [];

      function fetchPage(page) {
        return fetch(shopRoot() + '/products.json?limit=' + CONFIG.pageSize + '&page=' + page, {
          headers: { Accept: 'application/json' }
        })
          .then(function (r) { return r.ok ? r.json() : { products: [] }; })
          .then(function (data) {
            var products = (data && data.products) || [];
            for (var i = 0; i < products.length; i++) {
              var p = products[i];
              var skus = [];
              var variants = p.variants || [];
              for (var v = 0; v < variants.length; v++) {
                if (variants[v].sku) skus.push(variants[v].sku);
              }
              collected.push({
                t: p.title,
                h: p.handle,
                s: skus,
                i: (p.images && p.images[0] && p.images[0].src) || '',
                p: (variants[0] && variants[0].price) || ''
              });
            }
            if (products.length === CONFIG.pageSize && page < CONFIG.maxPages) {
              return fetchPage(page + 1);
            }
            return collected;
          });
      }

      return fetchPage(1).then(function (items) {
        self._writeCache(items);
        return items;
      });
    },

    load: function () {
      var self = this;
      if (this.ready) return Promise.resolve(this.items);
      if (this.loading) return this.loading;

      var cached = this._readCache();
      if (cached) {
        this._prepare(cached);
        return Promise.resolve(this.items);
      }

      this.loading = this._fetchAll()
        .then(function (items) {
          self._prepare(items);
          self.loading = null;
          return self.items;
        })
        .catch(function (err) {
          self.loading = null;
          throw err;
        });
      return this.loading;
    },

    search: function (query, limit) {
      if (!this.ready) return [];
      var qc = normalizeCompact(query);
      var ql = (query || '').trim().toLowerCase();
      if (qc.length < CONFIG.minQueryLength) return [];

      var scored = [];
      for (var i = 0; i < this.items.length; i++) {
        var it = this.items[i];
        var score = -1;
        if (it._sku) {
          if (it._sku.indexOf(qc) === 0) score = 0;            // SKU starts with fragment
          else if (it._sku.indexOf(qc) !== -1) score = 1;      // SKU contains fragment
        }
        if (score === -1 && it._title && it._title.indexOf(ql) !== -1) score = 2; // title contains
        if (score !== -1) scored.push({ item: it, score: score });
      }
      scored.sort(function (a, b) {
        if (a.score !== b.score) return a.score - b.score;
        return (a.item._title || '').localeCompare(b.item._title || '');
      });
      return scored.slice(0, limit || CONFIG.pageLimit).map(function (s) { return s.item; });
    }
  };

  // --- Predictive dropdown augmentation --------------------------------------
  function buildDropdownItems(matches, startIndex) {
    var html = '';
    for (var i = 0; i < matches.length; i++) {
      var m = matches[i];
      var img = m.i
        ? '<img class="m-predictive-search__image" src="' + sizedImage(m.i, 150) + '" alt="" width="70" loading="lazy">'
        : '';
      html +=
        '<li id="m-predictive-search-option-skuproduct-' + (startIndex + i) + '"' +
        ' class="m-predictive-search__list-item" role="option" aria-selected="false">' +
        '<a href="' + productUrl(m.h) + '" class="m-predictive-search__item m-predictive-search__item--link-with-thumbnail" tabindex="-1">' +
        img +
        '<div class="m-predictive-search__item-content">' +
        '<p class="m-predictive-search__item-heading">' + escapeHtml(m.t) + '</p>' +
        (m.p ? '<span class="m-product-price__price">' + escapeHtml(formatPrice(m.p)) + '</span>' : '') +
        '</div></a></li>';
    }
    return html;
  }

  function enhanceDropdown(psEl) {
    var input = psEl.querySelector('input[type="search"]');
    var container = psEl.querySelector('[data-predictive-search]');
    if (!input || !container) return;
    var resultsRoot = container.querySelector('#m-predictive-search-results');
    if (!resultsRoot) return; // native hasn't rendered yet

    var query = input.value.trim();
    if (query.length < CONFIG.minQueryLength) return;

    if (!Index.ready) {
      var loadingTarget = resultsRoot || container;
      Index.load()
        .then(function () {
          hideLoading(loadingTarget);
          enhanceDropdown(psEl);
        })
        .catch(function () { hideLoading(loadingTarget); });
      if (!Index.ready) showLoading(loadingTarget, 'Loading products…');
      return;
    }

    hideLoading(resultsRoot || container);

    var matches = Index.search(query, CONFIG.dropdownLimit);
    if (!matches.length) return;

    // De-dupe against products already shown natively.
    var shown = {};
    var links = resultsRoot.querySelectorAll('a[href*="/products/"]');
    for (var l = 0; l < links.length; l++) {
      shown[handleFromHref(links[l].getAttribute('href'))] = true;
    }
    var extra = matches.filter(function (m) { return !shown[m.h]; });
    if (!extra.length) return;

    var ob = psEl.__skuObserver;
    if (ob) ob.disconnect();

    var existingList = resultsRoot.querySelector('#m-predictive-search-results-products-list');
    if (existingList) {
      existingList.insertAdjacentHTML('beforeend', buildDropdownItems(extra, 900));
    } else {
      var group =
        '<div class="m-predictive-search__result-group" data-sku-search-group><div>' +
        '<h2 class="m-predictive-search__heading h5">' + escapeHtml(skuHeading()) + '</h2>' +
        '<ul id="m-predictive-search-results-products-list" class="m-predictive-search__results-list" role="group">' +
        buildDropdownItems(extra, 900) +
        '</ul></div></div>';
      var keywords = resultsRoot.querySelector('#m-predictive-search-option-search-keywords');
      if (keywords) keywords.insertAdjacentHTML('beforebegin', group);
      else resultsRoot.insertAdjacentHTML('beforeend', group);
    }

    if (ob) ob.observe(container, { childList: true, subtree: true });
  }

  function skuHeading() {
    var el = document.querySelector('[data-sku-search-heading]');
    return (el && el.getAttribute('data-sku-search-heading')) || 'Products';
  }

  function initDropdowns() {
    var instances = document.querySelectorAll('predictive-search');
    instances.forEach(function (psEl) {
      var input = psEl.querySelector('input[type="search"]');
      var container = psEl.querySelector('[data-predictive-search]');
      if (!input || !container || psEl.__skuObserver) return;

      input.addEventListener('focus', function () { Index.load().catch(function () {}); });

      var pending;
      var observer = new MutationObserver(function () {
        clearTimeout(pending);
        pending = setTimeout(function () { enhanceDropdown(psEl); }, 60);
      });
      psEl.__skuObserver = observer;
      observer.observe(container, { childList: true, subtree: true });
    });
  }

  // --- Search results page fallback ------------------------------------------
  function buildCard(m) {
    var img = m.i
      ? '<span class="m-sku-card__media"><img src="' + sizedImage(m.i, 360) + '" alt="" loading="lazy"></span>'
      : '<span class="m-sku-card__media m-sku-card__media--empty"></span>';
    return (
      '<div class="m-search-item m:column">' +
      '<a class="m-sku-card" href="' + productUrl(m.h) + '">' +
      img +
      '<span class="m-sku-card__title">' + escapeHtml(m.t) + '</span>' +
      (m.s && m.s.length ? '<span class="m-sku-card__sku">' + escapeHtml(m.s[0]) + '</span>' : '') +
      (m.p ? '<span class="m-sku-card__price">' + escapeHtml(formatPrice(m.p)) + '</span>' : '') +
      '</a></div>'
    );
  }

  function initResultsFallback() {
    var fb = document.getElementById('m-client-search-fallback');
    if (!fb) return;
    var terms = fb.getAttribute('data-terms') || '';
    if (!terms.trim()) return;

    var loadPromise = Index.load();
    if (!Index.ready) {
      fb.classList.add('is-loading');
      fb.innerHTML = spinnerHtml('Loading products…');
    }
    loadPromise
      .then(function () {
        var matches = Index.search(terms, CONFIG.pageLimit);
        fb.classList.remove('is-loading');
        if (!matches.length) {
          fb.innerHTML = '';
          return;
        }

        var grid = '';
        for (var i = 0; i < matches.length; i++) grid += buildCard(matches[i]);

        var heading = fb.getAttribute('data-heading') || 'Matching products';
        fb.innerHTML =
          '<p class="m-sku-fallback__note">' + escapeHtml(heading) + ' (' + matches.length + ')</p>' +
          '<div class="m-search-result m:grid m:grid-2-cols md:m:grid-3-cols lg:m:grid-4-cols m-sku-fallback__grid">' + grid + '</div>';

        var emptyMsg = document.getElementById('m-search-empty-message');
        if (emptyMsg) emptyMsg.style.display = 'none';
      })
      .catch(function () {
        fb.classList.remove('is-loading');
        fb.innerHTML = '';
      });
  }

  function injectStyles() {
    if (document.getElementById('m-sku-search-styles')) return;
    var css =
      '.m-sku-fallback__note{text-align:center;margin:0 0 20px;font-weight:600;}' +
      '.m-sku-fallback__grid{--column-gap:30px;--row-gap:30px;}' +
      '.m-sku-card{display:flex;flex-direction:column;text-decoration:none;color:inherit;}' +
      '.m-sku-card__media{display:block;aspect-ratio:1/1;overflow:hidden;background:rgba(0,0,0,.04);border-radius:8px;}' +
      '.m-sku-card__media img{width:100%;height:100%;object-fit:cover;display:block;}' +
      '.m-sku-card__title{margin-top:10px;font-size:14px;line-height:1.3;}' +
      '.m-sku-card__sku{margin-top:4px;font-size:12px;opacity:.6;}' +
      '.m-sku-card__price{margin-top:4px;font-weight:600;}' +
      '@keyframes m-sku-search-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}' +
      '@keyframes m-sku-search-pulse{0%,100%{opacity:1}50%{opacity:.45}}' +
      '.m-sku-search-loading{display:flex;align-items:center;justify-content:center;padding:16px 20px;}' +
      '.m-sku-search-loading__inner{display:inline-flex;align-items:center;gap:12px;}' +
      '.m-sku-search-loading__spinner{display:inline-flex;color:#E72C8B;animation:m-sku-search-spin .7s linear infinite;}' +
      '.m-sku-search-loading__spinner svg{width:32px;height:32px;filter:drop-shadow(0 0 8px rgba(231,44,139,.55));}' +
      '.m-sku-search-loading__label{font-size:15px;font-weight:600;color:#E72C8B;animation:m-sku-search-pulse 1.2s ease-in-out infinite;}' +
      '#m-client-search-fallback.is-loading{display:flex;align-items:center;justify-content:center;min-height:120px;padding:24px;}';
    var style = document.createElement('style');
    style.id = 'm-sku-search-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  function init() {
    injectStyles();
    initDropdowns();
    initResultsFallback();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init dropdowns when sections reload in the theme editor.
  document.addEventListener('shopify:section:load', initDropdowns);

  window.MjmSkuSearch = Index;
})();
